/**
 * TileRenderer - Renders a single tile with header/footer slots
 *
 * This component is part of the new slot-based architecture and renders
 * tiles directly from GridCell data with support for tile-header and tile-footer.
 *
 * This is separate from the legacy PanelTile to allow gradual migration.
 */

import {MediaCardType} from '@wandb/weave/common/types/media';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useMemo} from 'react';

import {CustomRunNames} from '../../../../state/views/customRunNames/types';
import * as PanelTypes from '../../../../state/views/panel/types';
import {PartRefFromObjSchema} from '../../../../state/views/types';
import {getMediaMetadata} from '../../../../util/media';
import {getCustomRunName} from '../../../../util/runs';
import {TileMediaSpec} from '../../../MediaCard';
import {MediaBrowserPanelConfig} from '../../common';
import {getGroupedMediaDimensions} from '../../dimensions';
import {MediaBrowserPanelTileTrackingContextProvider} from '../../MediaBrowserPanelTileTrackingContext';
import {TileMediaLoader} from '../../TileMediaLoader';
import {GridCell} from '../types';

export type TileRendererProps = {
  cell: GridCell;
  tileIndex: number;
  cardWidth: number;
  cardHeight: number;
  config: MediaBrowserPanelConfig;
  mediaType: MediaCardType;
  mediaSliderKey: string;
  panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  disableRunLinks?: boolean;
  customRunNames?: CustomRunNames;
  tileHeaderControls?: React.ReactNode;
  tileFooterControls?: React.ReactNode;
};

/**
 * Renders a tile with optional header and footer slots.
 *
 * @param props - TileRendererProps
 * @returns React element
 */
export const TileRenderer: React.FC<TileRendererProps> = ({
  cell,
  tileIndex,
  cardWidth,
  cardHeight,
  config,
  mediaType,
  mediaSliderKey,
  panelRef,
  disableRunLinks,
  customRunNames,
  tileHeaderControls,
  tileFooterControls,
}) => {
  // Extract values conditionally to avoid accessing undefined properties
  const {run, step, mediaIndex, mediaKey, maskOptions} = cell.isEmpty
    ? ({} as any)
    : cell;
  const runSignature = cell.isEmpty ? undefined : cell.tileSpec.runSignature;

  const tileMediaSpec: TileMediaSpec | null = useMemo(
    () =>
      cell.isEmpty
        ? null
        : {
            mediaType,
            run,
            mediaIndex,
            mediaKey,
            mediaStep: step,
            mediaSliderKey,
            runSignature: runSignature!,
          },
    [
      cell.isEmpty,
      mediaType,
      run,
      mediaIndex,
      mediaKey,
      step,
      mediaSliderKey,
      runSignature,
    ]
  );

  const runNotes = cell.isEmpty ? undefined : run.notes;
  const customRunName = cell.isEmpty
    ? undefined
    : getCustomRunName(run.name, false, customRunNames);

  const tileAndPanelControls = useMemo(
    () => ({
      segmentationMaskControl: config.segmentationMaskConfig,
      boundingBoxControl: config.boundingBoxConfig,
    }),
    [config.segmentationMaskConfig, config.boundingBoxConfig]
  );

  const metadata = cell.isEmpty
    ? null
    : getMediaMetadata(run.history, mediaKey, step);
  const groupedDimensions = cell.isEmpty
    ? null
    : getGroupedMediaDimensions(metadata);

  // Create a stable key for the maskKeys to prevent unnecessary remounts
  const stableMaskKey =
    !cell.isEmpty && maskOptions
      ? [...maskOptions.maskKeys].sort().join(',')
      : '';
  const componentKey = `card-${tileIndex}-${mediaKey}-${stableMaskKey}`;

  // Memoize the props object to prevent unnecessary re-renders
  const tileMediaLoaderProps = useMemo(
    () =>
      cell.isEmpty || !tileMediaSpec
        ? null
        : {
            tileMediaSpec,
            attemptNonexistentSteps: !config.snapToExistingStep,
            run,
            runSignature,
            runNotes,
            customRunName,
            labels: [], // Labels are now in the tile header via slots
            globalStep: step,
            mediaKey,
            mediaIndex,
            width: cardWidth,
            height: cardHeight,
            mediaWidth: groupedDimensions?.width,
            mediaHeight: groupedDimensions?.height,
            actualSize: !!config.actualSize,
            controls: tileAndPanelControls,
            mediaPanelRef: panelRef,
            moleculeConfig: config.moleculeConfig,
            disableRunLink: disableRunLinks,
            maskOptions,
            config,
            omitCaptions: true,
          },
    [
      cell.isEmpty,
      tileMediaSpec,
      run,
      runSignature,
      runNotes,
      customRunName,
      step,
      mediaKey,
      mediaIndex,
      cardWidth,
      cardHeight,
      groupedDimensions?.width,
      groupedDimensions?.height,
      tileAndPanelControls,
      panelRef,
      disableRunLinks,
      maskOptions,
      config,
    ]
  );

  // Handle empty cells after all hooks
  if (cell.isEmpty) {
    return (
      <div
        className="ui card"
        style={{
          width: cardWidth,
          height: cardHeight,
        }}
      />
    );
  }

  return (
    <MediaBrowserPanelTileTrackingContextProvider
      key={componentKey}
      mediaKey={mediaKey}
      panelId={panelRef.id}
      runName={run.name}>
      <div className={`tile-wrapper w-full flex flex-col`}>
        <div className={`tile-${mediaType} flex flex-col`}>
          <Tailwind>
            {tileHeaderControls && (
              <div
                className="tile-header align-baseline text-ellipsis"
                style={{
                  maxWidth: cardWidth,
                  width: '100%',
                }}>
                {tileHeaderControls}
              </div>
            )}
            <div className="tile-content">
              {tileMediaLoaderProps && (
                <TileMediaLoader {...tileMediaLoaderProps} />
              )}
            </div>
            {tileFooterControls && (
              <div
                className="tile-footer"
                style={{
                  maxWidth: cardWidth,
                  width: '100%',
                }}>
                {tileFooterControls}
              </div>
            )}
          </Tailwind>
        </div>
      </div>
    </MediaBrowserPanelTileTrackingContextProvider>
  );
};
